import { FormikErrors } from 'formik';
import { TFunction } from '@spotify-internal/i18n-core/types';
import { findIllegalControlCharsInXml } from './findIllegalControlCharsInXml';
import { FormValues } from './types';

export const handleValidate = (
  formValues: FormValues,
  t: TFunction,
  selectedCustomDateRange: boolean,
) => {
  const errors: FormikErrors<FormValues> = {};
  const { startDate, endDate, exportFileName } = formValues;

  if (selectedCustomDateRange) {
    if (!startDate || startDate === '') {
      errors.startDate = t(
        'atlas.catalog_exports.date_range.validation.valid_start_date',
        'A valid start date is required',
        'Validation text that appears under start date input',
      );
    }
    if (!endDate || endDate === '') {
      errors.endDate = t(
        'atlas.catalog_exports.date_range.validation.valid_end_date',
        'A valid end date is required',
        'Validation text that appears under end date input',
      );
    }
  }

  // File names must meet Google Cloud Object naming requirements: https://cloud.google.com/storage/docs/objects#naming
  // Cannot exceed 1024 bytes when UTF-8 encoded
  if (new Blob([exportFileName]).size > 1024) {
    errors.exportFileName = t(
      'atlas.catalog_exports.export_name.validation.1024_chars',
      'Export name can’t be longer than 1,024 characters',
      'Text that appears if file name exceeds 1,024 characters',
    );
  }
  // Cannot use special characters except: - and _
  if (/[.!"`'#%&,:;<>=@{}~/\$\(\)\*\+\\\?\[\]\^\|]+/.test(exportFileName)) {
    errors.exportFileName = t(
      'atlas.catalog_exports.export_name.validation.special_characters',
      'Can’t contain special characters besides: - and _',
      'Text that appears if the filename uses special characters',
    );
  }
  // Cannot include carriage return or line feed characters
  if (/(\r\n|\r|\n)/g.test(exportFileName)) {
    errors.exportFileName = `${t(
      'atlas.catalog_exports.export_name.validation.cant_contain',
      'Can’t contain the following characters:',
      'Text that appears if the filename uses specific characters (I.e. "my-report-\n")',
    )}: \n \r`;
  }
  // Cannot include control characters that are illegal in XML 1.0 (#x7F–#x84 and #x86–#x9F)
  if (findIllegalControlCharsInXml(exportFileName)) {
    errors.exportFileName = `${t(
      'atlas.catalog_exports.export_name.validation.cant_contain',
      'Can’t contain the following characters:',
      'Text that appears if the filename uses specific characters (I.e. "my-report-\n")',
    )}: Control characters that are illegal in XML 1.0 (#x7F–#x84 and #x86–#x9F)`;
  }
  if (exportFileName === '.') {
    errors.exportFileName = `${t(
      'atlas.catalog_exports.export_name.validation.cant_name_export',
      'Can’t name the export',
      'Text that appears if filename is invalid (I.e. Can’t name the export “.well-known/acme-challenge/”)',
    )}: '.'`;
  }
  if (exportFileName === '..') {
    errors.exportFileName = `${t(
      'atlas.catalog_exports.export_name.validation.cant_name_export',
      'Can’t name the export',
      'Text that appears if filename is invalid (I.e. Can’t name the export “.well-known/acme-challenge/”)',
    )}: '..'`;
  }
  if (
    exportFileName.toLocaleLowerCase().startsWith('.well-known/acme-challenge/')
  ) {
    errors.exportFileName = `${t(
      'atlas.catalog_exports.export_name.validation.cant_name_export',
      'Can’t name the export',
      'Text that appears if filename is invalid (I.e. Can’t name the export “.well-known/acme-challenge/”)',
    )}: .well-known/acme-challenge/`;
  }

  return errors;
};
